import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Container,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { TopBar } from '../layout/TopBar';
import { BottomNavigation } from '../layout/BottomNavigation';
import { useNavigate } from 'react-router-dom';
import { createContactMessage } from '../services/contactService';

export const ContactPage: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim() || !message.trim()) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    try {
      setLoading(true);
      setError('');

      await createContactMessage(
        user?.uid || 'anonymous',
        user?.displayName || 'Visitante',
        user?.email || '',
        subject.trim(),
        message.trim()
      );

      setSuccess(true);
      setSubject('');
      setMessage('');

      setTimeout(() => {
        if (user) {
          navigate('/settings');
        } else {
          navigate('/landing');
        }
      }, 3000);
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setError('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
        <>
      <TopBar
        title="Contato"
        icon={<EmailIcon sx={{ fontSize: '0.875rem' }} />}
        showBackButton
        onBackClick={() => navigate(user ? '/settings' : '/landing')}
      />
      <Box
        sx={{
          backgroundColor: theme.colors.background,
          minHeight: '100vh',
          paddingTop: '70px',
          paddingBottom: '120px',
        }}
      >
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Card
            sx={{
              p: 3,
              backgroundColor: theme.colors.card,
              borderRadius: theme.radii.large,
              border: `1px solid ${theme.colors.border}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: theme.colors.text.primary,
                mb: 1,
              }}
            >
              Entre em contato
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.colors.text.secondary,
                mb: 3,
              }}
            >
              Envie sua mensagem e nossa equipe responderá em breve
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Mensagem enviada com sucesso! Você será redirecionado...
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  label="Assunto"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  fullWidth
                  required
                  disabled={loading || success}
                  placeholder="Ex: Dúvida sobre planos"
                />

                <TextField
                  label="Mensagem"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  fullWidth
                  required
                  multiline
                  rows={6}
                  disabled={loading || success}
                  placeholder="Descreva sua dúvida ou sugestão..."
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || success}
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.text.contrast,
                    borderRadius: theme.radii.medium,
                    padding: '12px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: theme.colors.primary,
                      opacity: 0.9,
                    },
                    '&:disabled': {
                      backgroundColor: theme.colors.primary,
                      opacity: 0.5,
                    },
                  }}
                >
                  {loading ? 'Enviando...' : 'Enviar mensagem'}
                </Button>

                <Typography
                  variant="caption"
                  sx={{
                    color: theme.colors.text.secondary,
                    textAlign: 'center',
                  }}
                >
                  Você também pode nos enviar um email diretamente para{' '}
                  <a
                    href="mailto:contato@hostly.com.br"
                    style={{ color: theme.colors.primary }}
                  >
                    contato@hostly.com.br
                  </a>
                </Typography>
              </Box>
            </form>
          </Card>
        </Container>
      </Box>
      {user && <BottomNavigation />}
        </>
  );
};
